"use client";

import { Core } from "@faire/design-tokens";
import { Language } from "@faire/web-api/indigofair/languages/Language";
import { useStrictLocalization } from "@faire/web/common/localization";
import { Row } from "@faire/web/slate/Layout";
import { VerticalLine } from "@faire/web/slate/Layout";
import { getSpacing } from "@faire/web/slate/spacing";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import React, { Suspense } from "react";
import styled from "styled-components";

import { TopOfPageContentWrapper } from "@faire/retailer/components/App/TopOfPageContent/Wrapper";
import { HeaderContainer } from "@faire/retailer/components/Header/styled";
import { LazyLanguageSelector } from "@faire/retailer/components/LanguageSelector/LazyLanguageSelector";
import { getAvailableLanguageSelectorLocales } from "@faire/retailer/serialized-data/getAvailableLanguageSelectorLocales";
import { getIsRobot } from "@faire/retailer/serialized-data/getIsRobot";
import { getSessionCountry } from "@faire/retailer/serialized-data/getSessionCountry";
import { useRetailerStore } from "@faire/retailer/stores/domain/RetailerStore";
import { useUserStore } from "@faire/retailer/stores/domain/UserStore";

import { HeaderDesktopLogo } from "../desktop/HeaderDesktopLogo";
import { LoginSection } from "../desktop/LoginSection";
import { HeaderMinimal } from "../HeaderMinimal";

import { FaireDirectHeaderBanner } from "./__internal__/FaireDirectHeaderBanner";

export const HeaderFaireDirect = () => {
  return (
    <>
      <FaireDirectHeaderBanner />
      <TopOfPageContentWrapper>
        <HeaderFaireDirectContent />
      </TopOfPageContentWrapper>
    </>
  );
};

const HeaderFaireDirectContent = () => {
  const { isDesktopAndAbove } = useViewport();
  const { strictLocalize } = useStrictLocalization();
  const { browserLevelLanguage, browserLevelLocaleKey } = useUserStore([
    "browserLevelLanguage",
    "browserLevelLocaleKey",
  ]);
  const { showLanguageSelector } = useRetailerStore(["showLanguageSelector"]);

  const availableLanguageSelectorLocales =
    getAvailableLanguageSelectorLocales();

  if (isDesktopAndAbove) {
    return (
      <HeaderContainer>
        <Wrapper>
          <HeaderDesktopLogo />
          <Row align="center">
            {showLanguageSelector ? (
              <>
                <Suspense fallback={null}>
                  <LazyLanguageSelector
                    eventKey="logged-out-fd-top"
                    browserLanguage={browserLevelLanguage as Language}
                    browserLocale={browserLevelLocaleKey}
                    availableLocales={availableLanguageSelectorLocales}
                    assignLocalizedUrl
                    showCountries={getIsRobot()}
                    sessionCountry={getSessionCountry()}
                  />
                </Suspense>
                <VerticalLine
                  $height={getSpacing("3x")}
                  $color={Core.border.muted}
                />
              </>
            ) : null}
            <LoginSection
              signUpCTAText={strictLocalize({
                defaultMessage: "Sign up for free",
                description: {
                  text: "Call to action to begin the sign up flow (CTA button text)",
                  img: "https://cdn.faire.com/fastly/b4af2f5790b4e0752944ac056de2906b679cd6fd241b5101f29e827cc2523f2f.png",
                },
              })}
            />
          </Row>
        </Wrapper>
      </HeaderContainer>
    );
  }

  return <HeaderMinimal />;
};

const Wrapper = styled(Row).attrs({
  justify: "space-between",
  align: "center",
})`
  width: 100%;
  padding: ${getSpacing(0, "5x")};
`;
